import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import { getStoreAnalytics } from "../../api/analyticsService";
import { getStoreDetail, getOrders } from "../../api/adminService";
import { formatCurrency, formatDate, OrderStatusByName } from "./constants";
import { PeriodSelector, StatCard, SectionCard, EmptyState, TrafficSourceBars, ProductRow, TopProductsCard, formatCount, formatDayLabel } from "./analyticsShared";

function MiniStat({ label, value }) {
  return (
    <Box sx={{ textAlign: "center", py: 1 }}>
      <Typography variant="h6" fontWeight={800}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function StoreAnalyticsDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [period, setPeriod] = useState("30d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [chartMetric, setChartMetric] = useState("revenue");
  const [data, setData] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    getStoreDetail(id)
      .then(setStore)
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    if (period === "custom" && (!customStart || !customEnd)) return;

    let active = true;
    setLoading(true);
    getStoreAnalytics({
      storeId: id,
      period,
      startDate: period === "custom" ? customStart : undefined,
      endDate: period === "custom" ? customEnd : undefined,
    })
      .then((result) => {
        if (!active) return;
        setData(result);
        setError("");
        return getOrders({ storeId: id, fromDate: result.periodStart, toDate: result.periodEnd, pageSize: 8 })
          .then((orders) => {
            if (active) setRecentOrders(orders.items ?? []);
          })
          .catch(() => {});
      })
      .catch(() => {
        if (active) setError("Could not load analytics. Is the API running?");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, period, customStart, customEnd]);

  const chartSeries = useMemo(() => {
    if (!data?.overview) return { labels: [], values: [] };
    const points = chartMetric === "revenue" ? data.overview.revenueSeries : data.overview.visitsSeries;
    if (!points) return { labels: [], values: [] };
    return {
      labels: points.map((p) => formatDayLabel(p.date)),
      values: points.map((p) => (chartMetric === "revenue" ? p.amount : p.count)),
    };
  }, [data, chartMetric]);

  const hasProductHealthIssues =
    data?.productsHealth &&
    (data.productsHealth.lowStock.length > 0 ||
      data.productsHealth.highViewsZeroSales.length > 0 ||
      data.productsHealth.zeroViews.length > 0);

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title="Back to Analytics">
            <IconButton onClick={() => navigate("/admin/analytics")} sx={{ border: "1px solid", borderColor: "divider" }}>
              <ArrowBackRoundedIcon />
            </IconButton>
          </Tooltip>
          <Avatar src={store?.logoUrl || undefined} variant="rounded" sx={{ width: 44, height: 44, bgcolor: "primary.main" }}>
            <StorefrontRoundedIcon />
          </Avatar>
          <Box>
            <Typography variant="h4">{store?.storeName || "Store analytics"}</Typography>
            <Typography variant="body2" color="text.secondary">
              {store?.city || "Performance, traffic, and engagement for this store."}
            </Typography>
          </Box>
        </Stack>
        <PeriodSelector
          period={period}
          onPeriodChange={setPeriod}
          customStart={customStart}
          customEnd={customEnd}
          onCustomChange={(start, end) => {
            setCustomStart(start);
            setCustomEnd(end);
          }}
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !data ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <StatCard
                icon={PaidOutlinedIcon}
                label="Revenue"
                value={formatCurrency(data.overview.revenueCurrent)}
                changePercent={data.overview.revenueChangePercent}
                color="primary"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <StatCard
                icon={ReceiptLongOutlinedIcon}
                label="Orders"
                value={formatCount(data.overview.ordersCurrent)}
                changePercent={data.overview.ordersChangePercent}
                color="secondary"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <StatCard
                icon={PhoneIphoneRoundedIcon}
                label="Visitors"
                value={formatCount(data.overview.visitorsCurrent)}
                changePercent={data.overview.visitorsChangePercent}
                color="info"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <StatCard
                icon={TrendingUpRoundedIcon}
                label="Conversion Rate"
                value={`${data.overview.conversionRateCurrent}%`}
                changePercent={data.overview.conversionRateChangePercent}
                color="success"
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, lg: 8 }}>
              <SectionCard
                title={chartMetric === "revenue" ? "Revenue over time" : "Store visits over time"}
                action={
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={chartMetric}
                    onChange={(e, v) => v && setChartMetric(v)}
                  >
                    <ToggleButton value="revenue" sx={{ textTransform: "none", px: 1.5 }}>
                      Revenue
                    </ToggleButton>
                    <ToggleButton value="visits" sx={{ textTransform: "none", px: 1.5 }}>
                      Visits
                    </ToggleButton>
                  </ToggleButtonGroup>
                }
              >
                {chartSeries.values.length > 0 ? (
                  <LineChart
                    height={260}
                    series={[
                      {
                        data: chartSeries.values,
                        color: "#12B886",
                        area: true,
                        showMark: true,
                        valueFormatter: (v) => (chartMetric === "revenue" ? formatCurrency(v) : v),
                      },
                    ]}
                    xAxis={[{ scaleType: "point", data: chartSeries.labels }]}
                    yAxis={[{ width: 50 }]}
                    grid={{ horizontal: true }}
                    margin={{ left: 0, right: 10, top: 20, bottom: 20 }}
                    sx={{
                      "& .MuiLineElement-root": { strokeWidth: 2 },
                      "& .MuiAreaElement-root": { fillOpacity: 0.12 },
                      "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { stroke: "#e2e8f0" },
                    }}
                  />
                ) : (
                  <EmptyState text="No data for this period yet." />
                )}
              </SectionCard>
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <SectionCard title="Traffic sources">
                <TrafficSourceBars sources={data.traffic.sources} />
              </SectionCard>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TopProductsCard data={data.topProducts} subtitle="Top 20 by metric, this period" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard title="Recent orders">
                {recentOrders.length > 0 ? (
                  <Stack divider={<Divider />}>
                    {recentOrders.map((order) => {
                      const statusInfo = OrderStatusByName[order.status] || { label: order.status, color: "default" };
                      return (
                        <Stack
                          key={order.orderId}
                          direction="row"
                          alignItems="center"
                          spacing={1.5}
                          sx={{ py: 1, cursor: "pointer" }}
                          onClick={() => navigate(`/admin/orders/${order.orderId}`)}
                        >
                          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography variant="body2" fontWeight={700} noWrap>
                                {order.orderNumber}
                              </Typography>
                              <Chip label={statusInfo.label} size="small" color={statusInfo.color} sx={{ height: 20, fontSize: 11 }} />
                            </Stack>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                              {order.customerName} &middot; {formatDate(order.createdAt)}
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0 }}>
                            {formatCurrency(order.totalAmount)}
                          </Typography>
                        </Stack>
                      );
                    })}
                  </Stack>
                ) : (
                  <EmptyState text="No orders in this period yet." />
                )}
              </SectionCard>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <SectionCard title="Conversion">
                <Grid container>
                  <Grid size={6}>
                    <MiniStat label="Store visits" value={formatCount(data.traffic.storeVisits)} />
                  </Grid>
                  <Grid size={6}>
                    <MiniStat label="Product views" value={formatCount(data.traffic.productViewsTotal)} />
                  </Grid>
                  <Grid size={6}>
                    <MiniStat label="Add-to-cart rate" value={`${data.conversion.addToCartRate}%`} />
                  </Grid>
                  <Grid size={6}>
                    <MiniStat label="Cart abandonment" value={`${data.conversion.cartAbandonmentRate}%`} />
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <SectionCard title="Customers">
                <Grid container sx={{ mb: 1 }}>
                  <Grid size={4}>
                    <MiniStat label="New buyers" value={formatCount(data.customers.newBuyers)} />
                  </Grid>
                  <Grid size={4}>
                    <MiniStat label="Returning" value={formatCount(data.customers.returningBuyers)} />
                  </Grid>
                  <Grid size={4}>
                    <MiniStat label="Followers" value={formatCount(data.customers.followerCount)} />
                  </Grid>
                </Grid>
                {data.customers.byCity.length > 0 && (
                  <Stack spacing={0.75} sx={{ mt: 1 }}>
                    {data.customers.byCity.slice(0, 5).map((c) => (
                      <Stack key={c.city} direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          {c.city}
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {c.orderCount}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </SectionCard>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <SectionCard title="Ratings &amp; engagement">
                <Grid container sx={{ mb: 1 }}>
                  <Grid size={4}>
                    <MiniStat label="Avg. rating" value={data.engagement.averageRating > 0 ? data.engagement.averageRating.toFixed(1) : "-"} />
                  </Grid>
                  <Grid size={4}>
                    <MiniStat label="Messages" value={formatCount(data.engagement.messageCount)} />
                  </Grid>
                  <Grid size={4}>
                    <MiniStat
                      label="Avg. response"
                      value={data.engagement.averageResponseTimeMinutes != null ? `${Math.round(data.engagement.averageResponseTimeMinutes)}m` : "-"}
                    />
                  </Grid>
                </Grid>
                {data.engagement.recentReviews.length > 0 && (
                  <Stack spacing={1} divider={<Divider />} sx={{ mt: 1 }}>
                    {data.engagement.recentReviews.slice(0, 3).map((r) => (
                      <Box key={r.storeReviewId}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="body2" fontWeight={600}>
                            {r.customerName}
                          </Typography>
                          <StarRoundedIcon sx={{ fontSize: 14, color: "warning.main" }} />
                          <Typography variant="caption">{r.rating}</Typography>
                        </Stack>
                        {r.comment && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                            {r.comment}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                )}
              </SectionCard>
            </Grid>
          </Grid>

          {hasProductHealthIssues && (
            <SectionCard title="Product health" subtitle="Things worth a look">
              <Grid container spacing={2}>
                {data.productsHealth.lowStock.length > 0 && (
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                      Low stock
                    </Typography>
                    <Stack divider={<Divider />}>
                      {data.productsHealth.lowStock.slice(0, 5).map((p) => (
                        <ProductRow key={p.productId} product={p} metricLabel="Low stock" metricValue={`${p.stockQuantity} left`} />
                      ))}
                    </Stack>
                  </Grid>
                )}
                {data.productsHealth.highViewsZeroSales.length > 0 && (
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                      Lots of views, no sales
                    </Typography>
                    <Stack divider={<Divider />}>
                      {data.productsHealth.highViewsZeroSales.slice(0, 5).map((p) => (
                        <ProductRow key={p.productId} product={p} metricLabel={`${p.viewCount} views`} metricValue="0 sold" />
                      ))}
                    </Stack>
                  </Grid>
                )}
                {data.productsHealth.zeroViews.length > 0 && (
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                      No views yet
                    </Typography>
                    <Stack divider={<Divider />}>
                      {data.productsHealth.zeroViews.slice(0, 5).map((p) => (
                        <ProductRow key={p.productId} product={p} metricLabel="Needs better title/photos" metricValue="0 views" />
                      ))}
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </SectionCard>
          )}
        </>
      ) : null}
    </Box>
  );
}
